import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import InputOutlet from 'components/inputOutlet'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import useGetTransparency from '../memberTransparency/hooks/useGetTransparency'
import useSetFundSupport, { TSupportErrors } from './hooks/useSetFundSupport'

type TKey = keyof ISupportSetting

const FIELDS: Array<{
    key: TKey
    suggestion: keyof ISupportSuggestions
    label: string
    placeholder: string
    help: string
    /** the chip that fills in the manager's own */
    own: string
    inputMode: 'tel' | 'text'
}> = [
    {
        key: 'support_bale',
        suggestion: 'bale',
        label: 'حساب بله',
        placeholder: '@hamyan',
        help: 'شناسهٔ بلهٔ خودتان یا لینک گروه صندوق در بله.',
        own: 'حساب بلهٔ خودم',
        inputMode: 'text',
    },
    {
        key: 'support_eitaa',
        suggestion: 'eitaa',
        label: 'حساب ایتا',
        placeholder: '@hamyan',
        help: 'شناسهٔ ایتای خودتان یا لینک گروه صندوق در ایتا. شماره تلفن شناسه نیست.',
        own: 'حساب ایتای خودم',
        inputMode: 'text',
    },
    {
        key: 'support_phone',
        suggestion: 'phone',
        label: 'شماره تلفن',
        placeholder: '09123456789',
        help: 'موبایل، یا تلفن ثابت با پیش‌شماره.',
        own: 'شمارهٔ خودم',
        inputMode: 'tel',
    },
]

const EMPTY: ISupportSetting = {
    support_phone: '',
    support_bale: '',
    support_eitaa: '',
}

/** how a stored/suggested value reads on the chip */
const shown = (key: TKey, value: string) =>
    key === 'support_phone' || value.includes('/') ? value : `@${value}`

/**
 * «پشتیبانی صندوق» — the Bale account, Eitaa account and phone number this
 * fund's members reach its managers on. Each is optional; the fund page shows
 * «تماس با پشتیبانی صندوق» once any is set. The server normalises and
 * validates (see the backend's setting/support.py), so the form sends what
 * was typed and shows the server's message under the field it refused.
 */
export default function FundSupport() {
    const { id } = useParams()
    const { data, isLoading, refetch } = useGetTransparency()
    const server = data?.support_setting
    const suggestions = data?.support_suggestions
    const [form, setForm] = useState<ISupportSetting | undefined>()
    const [errors, setErrors] = useState<TSupportErrors>({})

    // Seed from the server once it answers (see BankerAccess), and again
    // after a save so the normalised form (`@x` → `x`) is what stays on screen.
    useEffect(() => {
        if (server) setForm({ ...EMPTY, ...server })
    }, [server])

    useEffect(() => {
        if (id) posthog.capture('fund_support_settings_viewed', { fund_id: id })
    }, [id])

    const { mutate, isLoading: isSaving } = useSetFundSupport(
        refetch,
        setErrors
    )

    const set = (key: TKey, value: string) => {
        if (!form) return
        setForm({ ...form, [key]: value })
        if (errors[key]) setErrors({ ...errors, [key]: undefined })
    }

    return (
        <div className="  flex flex-col bg-white h-full">
            <div className=" grow-0">
                <Header title="تنظیمات صندوق" />
            </div>
            <div className=" grow px-2">
                <p className=" text-lg my-2">پشتیبانی صندوق</p>
                <p className=" text-sm leading-6">
                    راه‌هایی که اعضا با آن‌ها با شما در ارتباط باشند. اعضا در
                    صفحهٔ صندوق «تماس با پشتیبانی صندوق» را می‌بینند و با یک
                    لمس در بله یا ایتا پیام می‌دهند یا تماس می‌گیرند. هر کدام را
                    که نمی‌خواهید خالی بگذارید.
                </p>
                <hr className=" my-3" />
                {isLoading || (server && !form) ? (
                    <DotsLoading />
                ) : !server || !form ? (
                    <p className=" my-3 px-2 text-sm text-gray-1">
                        تنظیمات پشتیبانی در دسترس نیست. کمی بعد دوباره امتحان
                        کنید.
                    </p>
                ) : (
                    FIELDS.map((field) => {
                        const own = suggestions?.[field.suggestion] ?? ''
                        const offerOwn = !!own && form[field.key] !== own
                        return (
                            <div key={field.key} className=" my-4 px-2">
                                <InputOutlet
                                    label={field.label}
                                    name={field.key}
                                    value={form[field.key]}
                                    placeholder={field.placeholder}
                                    onChange={(event) =>
                                        set(
                                            field.key,
                                            (event.target as HTMLInputElement)
                                                .value
                                        )
                                    }
                                    dir="ltr"
                                    style={{ textAlign: 'right' }}
                                    inputMode={field.inputMode}
                                    autoComplete="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    touched={!!errors[field.key]}
                                    errors={errors[field.key]}
                                />
                                {!errors[field.key] && (
                                    <p className=" mt-1 px-1 text-xs leading-5 text-text-secondary">
                                        {field.help}
                                    </p>
                                )}
                                {offerOwn && (
                                    <button
                                        type="button"
                                        className=" mt-2 rounded-full border border-brand px-3 py-1 text-xs text-brand-deep active:scale-[0.98]"
                                        onClick={() => {
                                            set(field.key, own)
                                            posthog.capture(
                                                'fund_support_suggestion_used',
                                                {
                                                    fund_id: id,
                                                    field: field.suggestion,
                                                }
                                            )
                                        }}
                                    >
                                        {field.own}:{' '}
                                        <span dir="ltr">
                                            {shown(field.key, own)}
                                        </span>
                                    </button>
                                )}
                            </div>
                        )
                    })
                )}
                <hr className=" my-3" />
                <p className=" px-2 text-xs leading-5 text-gray-1">
                    این راه‌ها به همهٔ اعضای صندوق نشان داده می‌شود؛ اگر
                    نمی‌خواهید شمارهٔ شخصی‌تان را ببینند، فقط بله یا ایتا
                    بگذارید.
                </p>
            </div>
            <div className=" grow-0">
                <div className=" bg-white py-4 px-4">
                    <button
                        className=" w-full bg-green-1 rounded-2xl text-white py-3 disabled:opacity-60"
                        disabled={!form || isSaving}
                        onClick={() => {
                            if (!form) return
                            mutate({ moneyPool: id, ...form })
                        }}
                    >
                        ثبت تغییرات
                    </button>
                </div>
            </div>
        </div>
    )
}
